<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Stat, Stats } from './types';
import { TIME_STATS } from './timeStats.const';

const props = defineProps<{
  compact?: boolean;
  isDirectValue?: boolean;
  loading?: boolean;
  stats: Stats;
}>();

const { t } = useI18n();

const statsArray = computed<Stat[]>(() => {
  return Array.isArray(props.stats) ? props.stats : [];
});

const formattedValue = computed(() => (name: string, value: string | number) => {
  if (TIME_STATS.includes(name)) return value + ' ' + t('common.day', Number(value));

  if (typeof value === 'number') return value < 0 ? `$(${Math.abs(value)})` : value;

  const numericValue = Number(value.replace(/[^0-9.-]+/g, ''));
  if (numericValue < 0) return value.replace('-', '').replace('$', '$(') + ')';

  return value || '-';
});

const statsValueClasses = computed(() => (value: string | number) => {
  const numericValue = typeof value === 'number' ? value : Number(value.replace(/[^0-9.-]+/g, ''));
  return [
    'w-fit flex-none text-gray-800',
    props.compact ? 'text-sm' : 'text-3xl font-medium leading-10 tracking-tight',
    numericValue < 0 ? 'text-red-500' : '',
  ];
});
const directStats = computed(() =>
  Object.entries(props.stats).flatMap(([key, value]) =>
    typeof value !== 'object'
      ? [{ key, value }]
      : Object.entries(value).map(([subKey, subValue]) => ({
          key: `${key} (${subKey})`,
          value: subValue,
        }))
  )
);
const checkForStats = computed(() => (Array.isArray(props.stats) ? props.stats.length : props.stats));
</script>

<template>
  <dl
    v-if="!isDirectValue && !loading && checkForStats"
    :class="[
      'grid w-fit max-w-full auto-cols-auto grid-flow-col gap-px divide-x overflow-auto rounded-md border bg-white lg:max-w-[768px] xl:max-w-full',
    ]"
  >
    <div
      v-for="{ name, change, changeType, value } in statsArray"
      :class="[
        'flex flex-col items-baseline justify-between',
        compact ? 'px-4 py-2' : 'gap-x-4 gap-y-2 px-4 py-10 sm:px-6 xl:px-8',
      ]"
      :key="name"
    >
      <dt
        :class="[
          'text-gray-500',
          compact ? 'text-nowrap text-xs font-bold uppercase tracking-wider' : 'text-sm font-medium leading-6',
        ]"
      >
        {{ $t(name) }}
      </dt>

      <dd
        v-if="change"
        :class="[changeType === 'negative' ? 'text-rose-600' : 'text-gray-700', 'text-xs font-medium']"
      >
        {{ change }}
      </dd>

      <dd :class="statsValueClasses(value)">
        {{ formattedValue(name, value) }}
      </dd>
    </div>
  </dl>

  <dl
    v-else-if="isDirectValue && !loading && checkForStats"
    :class="[
      'grid w-fit max-w-full auto-cols-auto grid-flow-col gap-px divide-x overflow-auto rounded-md border bg-white lg:max-w-[768px] 2xl:max-w-full',
    ]"
  >
    <div
      v-for="{ key, value } in directStats"
      :class="[
        'flex flex-wrap items-baseline justify-between',
        compact ? 'px-4 py-2' : 'gap-x-4 gap-y-2 px-4 py-10 sm:px-6 xl:px-8',
      ]"
      :key="key"
    >
      <dt
        :class="[
          'text-gray-500',
          compact ? 'text-xs font-bold uppercase tracking-wider' : 'text-sm font-medium leading-6',
        ]"
      >
        {{ key }}
      </dt>

      <dd
        :class="[
          'w-full flex-none text-gray-900',
          compact ? 'text-sm' : 'text-3xl font-medium leading-10 tracking-tight',
        ]"
      >
        {{ value }}
      </dd>
    </div>
  </dl>
</template>
